<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="filter-block">
          <div class="w-50">
            <BlockHeader :name="userId ? 'User details' : 'Team details'" class="mt-0 mb-0" />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 col-sm-12 col-xs-12">
            <Input v-if="userId" v-model="userData.username" form-group-id="Name" placeholder="Name" :bottom="20" viewMode />
            <Input v-else v-model="teamData.name" form-group-id="Name" placeholder="Name" :bottom="20" viewMode />
          </div>
          <div class="col-lg-6 col-sm-12 col-xs-12">
            <Input v-if="userId" v-model="userData.email" form-group-id="Email" placeholder="Email" :bottom="20" viewMode />
            <Input v-else v-model="teamData.description" form-group-id="Description" placeholder="Description" :bottom="20" viewMode />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 col-sm-12 col-xs-12">
            <p class="mb-0">
              {{ userId ? $translate('User account configuration') : $translate('Team account configuration') }}
            </p>
            <div class="mt-1 ml-5">
              <Trigger
                :value="userAccountConfiguration"
                form-group-id="userAccountConfiguration"
                placeholder="Enable"
                :input="(v) => setExcludeFromPDFValue(group.id, v)"
                :bottom="0"
              />
            </div>
          </div>
          <div class="col-lg-6 col-sm-12 col-xs-12">
            <p class="mb-0">
              {{ $translate('Access to all files') }}
            </p>
            <div class="mt-1 ml-5">
              <Trigger
                :value="isFullAccess"
                form-group-id="isFullAccess"
                :placeholder="isFullAccess ? 'Enable' : 'Disable'"
                :input="onFullAccessChange"
                :bottom="0"
              />
            </div>
          </div>
        </div>
        <div v-if="generalStructure.length" class="mt-4 overflow-hidden d-flex flex-column">
          <BlockHeader name="Assignments" class="mt-0 mb-0" />
          <p class="description-text">
            {{ userId ? $translate('Select the folders which should be accessible for this user') : $translate('Select the folders which should be accessible for this team') }}
          </p>
          <BOverlay
            :show="isAccessChangeLoading"
            class="overflow-hidden d-flex flex-column"
            spinner-variant="primary"
            spinner-type="grow"
            spinner-small
            rounded="sm"
          >
            <div class="folder-tree-wrapper overflow-auto overflow-x-hidden">
              <FolderTree
                v-for="value in generalStructure"
                :key="value.id"
                :value="value"
                :user-access-list="accessList"
                :on-change-tree-handler="onChangeTreeHandler"
                :on-user-access-change="onAccessChange"
              />
            </div>
          </BOverlay>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BOverlay } from 'bootstrap-vue';
import { ReportsMixin, UserMixin, NotificationMixin } from '../../../../mixins';
import BlockHeader from '../../common/block-header';
import Input from '../../common/input';
import Trigger from '../../common/trigger';
import api from '../../../api';

import FolderTree from './folder-tree';

export default {
  name: 'PermissionsDetails',
  components: { BlockHeader, Input, Trigger, FolderTree, BOverlay },
  mixins: [ReportsMixin, UserMixin, NotificationMixin],
  data() {
    return {
      userData: {},
      teamData: {},
      userId: '',
      teamId: '',
      accessList: [],
      userAccountConfiguration: true,
      isFullAccess: false,
      generalStructure: [],
      rootFolderData: {
        childList: [],
        id: 'mainFolder',
        isActive: true,
        isOpen: true,
        name: this.$translate('Main folder (top level)'),
        nestingCount: -1,
        type: 'folder',
      },
      isSelectedAll: false,
      fullDataLength: 0,
      isAccessChangeLoading: false,
    };
  },
  async created() {
    this.userId = this.$route.params.userId;
    this.teamId = this.$route.params.teamId;
    if (this.userId || this.teamId) {
      const generalStructure = await api.viewStructure.getStructure();
      this.generalStructure = [{ ...this.rootFolderData, childList: generalStructure.resultList }];
      this.fullDataLength = generalStructure.fullDataLength;

      if (this.userId) {
        const user = await this.userService.getById(this.userId);
        this.userData = user.data;
        this.isFullAccess = user.data.isFullViewStructureAccess;

        await this.getAccess({ id: this.userId, type: 'user' });
      } else if (this.teamId) {
        const team = await this.reportsService.getTeamByID(this.teamId);
        this.teamData = team.data;
        this.isFullAccess = team.data.isFullViewStructureAccess;

        await this.getAccess({ id: this.teamId, type: 'team' });
      }
    }
  },
  methods: {
    async getAccess({ id, type }) {
      const getAccessFunction = type === 'user' ? api.viewPermissions.getUserAccess : api.viewPermissions.getTeamAccess;
      const accessList = await getAccessFunction(id);
      this.accessList = accessList;
      this.isSelectedAll = accessList.length >= this.fullDataLength;
      if (this.isSelectedAll) {
        this.accessList.push(this.rootFolderData.id);
      }
    },
    findAndChangeItem(array, condition, newValue) {
      return array.map((item) => {
        if (condition(item)) {
          return { ...item, isOpen: newValue };
        } else if (Array.isArray(item.childList)) {
          return { ...item, childList: this.findAndChangeItem(item.childList, condition, newValue) };
        } else {
          return item;
        }
      });
    },
    onChangeTreeHandler(newValue) {
      const newGeneralStructure = this.findAndChangeItem(this.generalStructure, ({ id }) => id === newValue.id, !newValue.isOpen);
      this.generalStructure = newGeneralStructure;
    },
    getAllParentIds(data, itemId, parentIds = []) {
      for (const item of data) {
        if (item.id === itemId) {
          if (item.owned) {
            parentIds.push(item.owned);
            this.getAllParentIds(data, item.owned, parentIds);
          }
          break; // Stop searching once we've found the item and its parent(s)
        }
        if (item.childList && item.childList.length > 0) {
          this.getAllParentIds(item.childList, itemId, parentIds);
        }
      }
      return parentIds;
    },
    async onAccessChange(structure) {
      try {
        this.isAccessChangeLoading = true;
        if (this.userId) {
          if (structure.id === 'mainFolder') {
            await api.viewPermissions.updateUserAllAccess({ userId: this.userId, value: !this.isSelectedAll });
          } else {
            await api.viewPermissions.updateUserAccess({ userId: this.userId, structureId: structure.id });
          }
          await this.getAccess({ id: this.userId, type: 'user' });
        } else if (this.teamId) {
          if (structure.id === 'mainFolder') {
            await api.viewPermissions.updateTeamAllAccess({ teamId: this.teamId, value: !this.isSelectedAll });
          } else {
            await api.viewPermissions.updateTeamAccess({ teamId: this.teamId, structureId: structure.id });
          }
          await this.getAccess({ id: this.teamId, type: 'team' });
        }
        this.showNotification({ type: 'success', message: 'Access has been changed!' });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      } finally {
        this.isAccessChangeLoading = false;
      }
    },
    async onFullAccessChange(value) {
      try {
        this.isFullAccess = value;
        if (this.userId) {
          await api.viewPermissions.updateUserFullAccess({ userId: this.userId, value });
        } else if (this.teamId) {
          await api.viewPermissions.updateTeamFullAccess({ teamId: this.teamId, value });
        }
        this.showNotification({ type: 'success', message: 'Access has been changed!' });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.description-text {
  color: #646a87;
  font-family: Poppins;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin-top: 8px;
  margin-bottom: 12px;
}
.folder-tree-wrapper {
  border: 1px solid #c5d0de;
  border-radius: 4px;
}
.overflow-x-hidden {
  overflow-x: hidden;
}
::v-deep .checkbox label {
  padding-left: 0;
}
::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
